import { Anchor, Paragraph, ParagraphProps, SizableText } from '@my/ui'
import { LinkPreview } from 'app/features/shared/containers'
import { useState } from 'react'
import { wordToLink } from '../../helpers'

export interface PostTextProps {
  text?: string
  expandable?: boolean
}

export const PostText = ({ text, expandable, ...props }: PostTextProps & ParagraphProps) => {
  const [expanded, setExpanded] = useState(expandable ? false : true)

  if (!text) {
    return null
  }

  const foundLink = text
    .split('\n')
    .reduce((acc, line) => [...acc, ...line.split(/\s+/)], [])
    .find((word) => wordToLink(word).isLink)
  const link = foundLink ? wordToLink(foundLink).link : null

  let lettersRemaining = 200
  let stopWriting = false
  const lines = text.split('\n')
  const paragraphs = lines.map((line, index) => {
    // if we already stopped writing and we are not expanded, don't render anything
    if (stopWriting && !expanded) {
      return null
    }

    const words = line.split(/\s+/)
    const components = words.reduce((acc, word, index) => {
      lettersRemaining -= word.length + 1

      // if we already stopped writing and we are not expanded, don't render anything
      if (stopWriting) {
        return acc
      }

      // if we are not expanded and we reached the limit, stop writing
      if (!expanded && lettersRemaining < 0) {
        stopWriting = true
        return [...acc, '...']
      }

      const wordToLinkResult = wordToLink(word)

      if (!wordToLinkResult.isLink) {
        return [...acc, word + ' ']
      }

      return [
        ...acc,
        <Anchor
          key={index}
          href={wordToLinkResult.href}
          fow="500"
          textDecorationStyle="solid"
          textDecorationLine="underline"
          color="$overlay12"
          target="_blank"
          rel="noopener noreferrer"
        >
          {wordToLinkResult.link}
        </Anchor>,
        wordToLinkResult.afterLink ? wordToLinkResult.afterLink + ' ' : '',
      ]
    }, [])

    return (
      <Paragraph key={index} size="$4" color="$gray11" {...props}>
        {components}
      </Paragraph>
    )
  })

  return (
    <>
      {paragraphs}
      {stopWriting && !expanded && (
        <SizableText
          size="$4"
          color="$gray11"
          fow="500"
          onPress={(e) => {
            e.preventDefault()
            setExpanded(true)
          }}
          cursor="pointer"
        >
          Show more...
        </SizableText>
      )}
      {link && <LinkPreview url={link} />}
    </>
  )
}
